/* global console */
import utils from 'utils/utils';
import Model from 'utils/model';
import image from 'utils/image';
import { objectToString, prettyObject } from 'utils/string';

import config from 'player/config';
import Domain from 'player/model/domain';
import Token from 'player/model/token';

import locale from 'player/model/locale';

import appnexus from './ads/appnexus';
import addPlaylist from './config/playlist';
import { addSharing } from './config/sharing';
import setPlaybackOptions from './config/playback';
import addYoubora from './config/jw-youbora';
import setKey from './config/keys';
import setCaptions from './config/captions';

import * as STATUS_CODES from './config/status-codes';
import youbora from './youbora';

/**
 * Config instance for player
 * Contains SVP Player options
 * Parses config
 *
 * @param options
 * @constructor
 */
const Config = function () {
    this.attributes = {
        // DOM node
        node: null,
        // API vendor
        vendor: null,
        // player environment
        env: 'production',
        // stream object (extracted from asset)
        stream: null,
        // if not provided width will be read from node element
        width: '100%',
        // if not provided height will be read from node element
        height: '100%',
        // start playing from chapter time
        chapter: null,
        // start stream automatically on capable devices (does not work on iPhone and iPad, Android)
        autoplay: false,
        // player poster (image), mixed string (http src)|true|false
        poster: true,
        // display title on poster or change to provided string (default false)
        title: null,
        // play ahead time, format XXhYYmZZs, for example: 02h09m10s
        time: null,
        // repeat mode
        repeat: false,
        // minimum dvr window
        minDvrWindow: null,
        // url to css file with skin
        skin: config.skins.default,
        // right click defualt text and link
        about: {
            text: 'Stream',
            link: ''
        },
        // token function for secured streams
        token: null
    };

    /**
     * JW Player Config
     */
    this.jwDefaults = {
        primary: null,
        hlshtml: true,
        base: config.cdn.player,
        flashplayer: `${config.cdn.player}/jwplayer.flash.swf`,
        wmode: 'transparent',
        key: config.player.keys.default
    };

    this.token = null;
};

Config.prototype = {
    /**
     * Calculates bitrate value based on given limit
     *
     * maxBitrate is increased by 15% due to VBR encoding
     * @returns {number}
     */
    getMaxBitrate() {
        return this.get('maxBitrate') * 1.15;
    },

    setStream(stream) {
        this.stream = stream;
        this.token = new Token(stream.get('id'), this.getMaxBitrate());
    },

    /**
     * Get config passed to SVP Player constructor
     * @returns {*|{}}
     */
    getRaw() {
        return this.rawOptions || {};
    },

    /**
     * Dump Javascript config to a string
     * @returns {Promise<void>}
     */
    async dump() {
        const prettier = await prettyObject();
        return prettier(objectToString(this.getRaw()));
    },

    /**
         * TODO add tests
         *
         * @param stream
         * @param container
         * @returns {*}
         */
    getPoster(stream, playerContainer) {
        const container = playerContainer || document.getElementById(this.get('node'));

        const width = this.get('width');
        const height = this.get('height');

        if (utils.isString(this.get('poster'))) {
            return this.get('poster');
        }

        if (container && stream) {
            return image.getImageSrc(
                stream.getPoster(),
                'poster',
                // use width and height if it's set as a number
                utils.isNumber(width) ? width : container.clientWidth,
                utils.isNumber(height) ? height : container.clientHeight
            );
        }

        return null;
    },

    /**
         * Retrieve token required for secure streams
         * Token expiry has to match API
         */
    getToken() {
        if (this.stream.isSecure()) {
            return this.token.fetch(this.get('token'));
        }

        return Promise.resolve(null);
    },

    /**
         * Token is required only for secure streams so for all other types it's valid
         *
         * @returns {boolean}
         */
    hasValidToken() {
        return !this.stream.isSecure() || this.token.isValid();
    },

    getStreamUrl(type) {
        // stream not set yet
        if (!this.stream) {
            // eslint-disable-next-line no-console
            console.error('SVP SDK: getStreamUrl() called before stream set');
            return null;
        }

        return this.getToken().then((token) => {
            const streamUrl = this.stream.getUrl(type);
            const maxBitrate = this.getMaxBitrate();
            const params = [];

            if (type === 'hls' && token) {
                params.push(`hdnea=${encodeURIComponent(token)}`);
            }

            if (maxBitrate) {
                params.push(`b=0-${maxBitrate}`);
            }

            // only live streams require custom metadata
            // it's used to trigger ads
            if (this.stream.isLive()) {
                params.push('custom-mdt=on');
            }

            return streamUrl + ((params.length > 0) ? `?${params.join('&')}` : '');
        });
    },

    /**
         * Check if user is eligible to play stream in his geolocation
         *
         * @param stream
         * @returns {Promise}
         */
    isStreamPlayable() {
        const { stream } = this;
        const settings = this.getSettings();

        return new Promise((resolve, reject) => {
            if (settings.preview === true) {
                resolve(STATUS_CODES.ACTIVE_PREVIEW);
            } else if (stream.isActive()) {
                if (stream.isGeoblocked() && !stream.isFuture()) {
                    this.getStreamUrl('hls').then((streamUrl) => {
                        // fetch manifest to check user access
                        utils.ajax(streamUrl, resolve, function (...args) {
                            const response = args[2] || {};

                            if (response.status === 403) {
                                return reject(STATUS_CODES.NOT_ACTIVE_GEOBLOCKED);
                            }

                            return reject(STATUS_CODES.NETWORK_ERROR);
                        }, true);
                    }).catch(reject);
                } else {
                    resolve(STATUS_CODES.ACTIVE);
                }
            } else if (stream.isPast()) {
                reject(STATUS_CODES.NOT_ACTIVE_PAST);
            } else {
                reject(STATUS_CODES.NOT_ACTIVE);
            }
        });
    },

    getJwConfig(stream) {
        const settings = this.getSettings();

        return Promise.resolve({
            width: this.get('width'),
            height: this.get('height'),
            abouttext: this.get('about').text,
            aboutlink: this.get('about').link,
            sharing: utils.extend({}, config.sharing.global),
            primary: this.get('primary') || 'html5',
            localization: locale.translate('player', true),
            plugins: {},
            cast: {}
        }).then((jwConfig) => this.getStreamUrl('hls').then((streamUrl) => addPlaylist(
            streamUrl,
            stream,
            {
                poster: this.getPoster(stream),
                title: this.get('title') === true ? stream.get('title') : this.get('title'),
                locale: locale,
                minDvrWindow: this.get('minDvrWindow')
            },
            this.getPlaylistSettings()
        )(jwConfig)))
            .then(addSharing({
                options: this.get('sharing'),
                stream
            }))
            .then(setCaptions(this.get('captions')))
            .then(setPlaybackOptions(stream, {
                mute: this.get('mute'),
                skin: this.get('skin'),
                autoplay: this.get('autoplay'),
                autopause: this.get('autopause'),
                repeat: this.get('repeat')
            }))
            // enable only five percent of traffic for youbora
            .then(addYoubora(
                stream,
                youbora.state === 'READY'
            ))
            .then(setKey(this.get('vendor')))
            .then((jwConfig) => utils.merge(utils.extend({}, this.jwDefaults, jwConfig), settings.jw || {}));
    },

    /**
         * Check if recommended is available
         */
    hasRecommended() {
        // recommended truned on and repeat truned off
        return this.get('recommended') !== false && this.get('repeat') === false;
    },

    getRecommended() {
        return this.get('recommended');
    },

    hasNext() {
        if (!this.hasRecommended()) {
            return false;
        }

        if (this.getRecommended() && (this.getRecommended().next === false
                || typeof this.getRecommended().next === 'undefined')) {
            return false;
        }

        return true;
    },

    getPlaylistSettings() {
        return utils.extend({
            adn: this.get('adn'),
            hasNext: this.hasNext()
        }, this.getSettings());
    },

    getLiveMidrollTag(time) {
        return appnexus.getLiveMidrollTag(
            time,
            this.stream,
            this.getPlaylistSettings()
        );
    },

    parse(options) {
        const asset = (options.asset || options.id);
        const about = config.about[options.vendor];
        const skin = utils.extend({}, (config.skins[options.vendor] || config.skins.default));

        // set default skin for player if nothing is provided
        if (typeof options.skin === 'object') {
            if (options.skin.name) {
                skin.name += ` ${options.skin.name}`;
            }

            if (options.skin.url) {
                skin.url = options.skin.url;
            }
        } else {
            // remove if not valid
            delete options.skin;
        }


        options.skin = skin;

        // override about link/text
        if (!options.about && about) {
            options.about = about;
        }

        // delete asset
        if (asset) {
            delete options.asset;
        }

        // check if user passed chapter or time
        if (utils.isString(options.chapter) && options.chapter.match(/^\d+$/)) {
            options.chapter = parseInt(options.chapter, 10);
        }

        // legacy support
        if (options.time) {
            options.time = utils.time.shareTimeToSeconds(options.time);
        }

        if (options.settings) {
            delete options.settings;
        }

        return options;
    }
};

// Immutable method for restricted settings like preview or na
Object.defineProperty(Config.prototype, 'initialize', {
    value(options) {
        const { vendor } = options;
        const rawOptions = utils.extend({}, options);

        this.rawOptions = rawOptions;

        Domain.getPermissions(vendor).then((permissions) => {
            const settings = {};

            if (permissions.whitelist === true && options.settings) {
                utils.extend(settings, options.settings);
            }

            if (permissions.preview === false) {
                delete settings.preview;
            }

            // turn off ads for certain provider
            if (config.ads[vendor] === false) {
                settings.na = true;
            }

            Object.defineProperty(this, 'settings', {
                value: Object.freeze(settings),
                writable: false
            });

            utils.extend(this.attributes, this.parse(options));
            this.trigger('ready', rawOptions, settings);
        });
    },
    writable: false
});

// Immutable method for restricted settings like preview or na
Object.defineProperty(Config.prototype, 'getSettings', {
    value() {
        return this.settings;
    },
    writable: false
});

utils.extend(Config.prototype, Model);

export default Config;
